export enum NotificationSettingType {
  RESERVATION_CREATED_EXTERNAL = 'RESERVATION_CREATED_EXTERNAL',
  RESERVATION_CREATED_INTERNAL = 'RESERVATION_CREATED_INTERNAL',
  RESERVATION_CANCELLED = 'RESERVATION_CANCELLED',
  RESERVATION_CHECKED_IN = 'RESERVATION_CHECKED_IN',
  RESERVATION_REQUEST_CREATED = 'RESERVATION_REQUEST_CREATED',
  TICKET_CHECKED_IN = 'TICKET_CHECKED_IN',
  PAYMENT_LINK_COMPLETED = 'PAYMENT_LINK_COMPLETED',
  CUSTOMER_CHAT_NEEDS_RESPONSE = 'CUSTOMER_CHAT_NEEDS_RESPONSE',
  SCHEDULED_REPORT_EMAIL = 'SCHEDULED_REPORT_EMAIL',
  ADMIN_APP_NOTIFICATIONS = 'ADMIN_APP_NOTIFICATIONS',
}

export enum NotificationSettingCategory {
  RESERVATIONS = 'RESERVATIONS',
  RESERVATION_REQUESTS = 'RESERVATION_REQUESTS',
  PAYMENT_LINKS = 'PAYMENT_LINKS',
  TICKETS = 'TICKETS',
  CUSTOMER_CHAT = 'CUSTOMER_CHAT',
  REPORTS = 'REPORTS',
  APP_NOTIFICATIONS = 'APP_NOTIFICATIONS',
}

export type NotificationSettingChannel = 'email' | 'sms' | 'push'

export type NotificationSettingTypeDescriptor = {
  name: string
  value: NotificationSettingType
  category: NotificationSettingCategory
  channels: NotificationSettingChannel[]
  /** ADMIN-only types are not shown to non-admin management users. */
  adminOnly?: boolean
  /** Whether the setting is scoped per venue. When false, the setting is global per management user. */
  perVenue: boolean
}

export const notificationSettingTypes: Record<
  NotificationSettingType,
  NotificationSettingTypeDescriptor
> = {
  [NotificationSettingType.RESERVATION_CREATED_EXTERNAL]: {
    name: 'Created externally (widgets, promoters, Tablelist)',
    value: NotificationSettingType.RESERVATION_CREATED_EXTERNAL,
    category: NotificationSettingCategory.RESERVATIONS,
    channels: ['email', 'sms', 'push'],
    perVenue: true,
  },
  [NotificationSettingType.RESERVATION_CREATED_INTERNAL]: {
    name: 'Created internally (staff)',
    value: NotificationSettingType.RESERVATION_CREATED_INTERNAL,
    category: NotificationSettingCategory.RESERVATIONS,
    channels: ['email', 'sms', 'push'],
    perVenue: true,
  },
  [NotificationSettingType.RESERVATION_CANCELLED]: {
    name: 'Cancelled',
    value: NotificationSettingType.RESERVATION_CANCELLED,
    category: NotificationSettingCategory.RESERVATIONS,
    channels: ['email', 'sms', 'push'],
    perVenue: true,
  },
  [NotificationSettingType.RESERVATION_CHECKED_IN]: {
    name: 'Checked in',
    value: NotificationSettingType.RESERVATION_CHECKED_IN,
    category: NotificationSettingCategory.RESERVATIONS,
    channels: ['push'],
    perVenue: true,
  },
  [NotificationSettingType.RESERVATION_REQUEST_CREATED]: {
    name: 'Created',
    value: NotificationSettingType.RESERVATION_REQUEST_CREATED,
    category: NotificationSettingCategory.RESERVATION_REQUESTS,
    channels: ['email', 'sms', 'push'],
    perVenue: true,
  },
  [NotificationSettingType.TICKET_CHECKED_IN]: {
    name: 'Checked in',
    value: NotificationSettingType.TICKET_CHECKED_IN,
    category: NotificationSettingCategory.TICKETS,
    channels: ['sms'],
    perVenue: true,
  },
  [NotificationSettingType.PAYMENT_LINK_COMPLETED]: {
    name: 'Completed',
    value: NotificationSettingType.PAYMENT_LINK_COMPLETED,
    category: NotificationSettingCategory.PAYMENT_LINKS,
    channels: ['email', 'sms', 'push'],
    perVenue: true,
  },
  [NotificationSettingType.CUSTOMER_CHAT_NEEDS_RESPONSE]: {
    name: 'Needs response',
    value: NotificationSettingType.CUSTOMER_CHAT_NEEDS_RESPONSE,
    category: NotificationSettingCategory.CUSTOMER_CHAT,
    channels: ['email', 'push'],
    perVenue: true,
  },
  [NotificationSettingType.SCHEDULED_REPORT_EMAIL]: {
    name: 'Scheduled report emails',
    value: NotificationSettingType.SCHEDULED_REPORT_EMAIL,
    category: NotificationSettingCategory.REPORTS,
    channels: ['email'],
    perVenue: true,
  },
  [NotificationSettingType.ADMIN_APP_NOTIFICATIONS]: {
    name: 'App notifications (new account signups, etc.)',
    value: NotificationSettingType.ADMIN_APP_NOTIFICATIONS,
    category: NotificationSettingCategory.APP_NOTIFICATIONS,
    channels: ['email'],
    adminOnly: true,
    perVenue: false,
  },
}

export const NOTIFICATION_SETTING_CATEGORY_LABELS: Record<
  NotificationSettingCategory,
  string
> = {
  [NotificationSettingCategory.RESERVATIONS]: 'Reservations',
  [NotificationSettingCategory.RESERVATION_REQUESTS]: 'Reservation requests',
  [NotificationSettingCategory.PAYMENT_LINKS]: 'Payment links',
  [NotificationSettingCategory.TICKETS]: 'Tickets',
  [NotificationSettingCategory.CUSTOMER_CHAT]: 'Customer chat',
  [NotificationSettingCategory.REPORTS]: 'Reports',
  [NotificationSettingCategory.APP_NOTIFICATIONS]: 'App notifications',
}

export const NOTIFICATION_SETTING_ERROR = {
  ORGANIZATION_MEMBER_REQUIRED: 'organizationMember is required',
  MANAGEMENT_USER_REQUIRED: 'managementUser is required',
  ORGANIZATION_REQUIRED: 'organization is required',
  VENUE_REQUIRED: 'venue is required',
  TYPE_REQUIRED: 'type is required',
  TYPE_INVALID: 'type is invalid',
  NOTIFICATIONS_REQUIRED: 'notifications is required',
  VENUE_NOT_FOUND: 'venue not found',
  ORGANIZATION_MEMBER_NOT_FOUND: 'organization member not found',
  ORGANIZATION_MEMBER_NOT_IN_VENUE: 'organization member not in venue',
  MANAGEMENT_USER_NOT_FOUND: 'management user not found',
  NOTIFICATION_SETTINGS_NOT_FOUND: 'notification settings not found',
  NOTIFICATION_VALUES_INVALID: 'notification settings values are invalid',
  DUPLICATE_TYPE:
    'a notification setting of this type for this venue already exists',
  ADMIN_ONLY_TYPE: 'this notification setting type is only available to admins',
} as const
